import { isArray, omit } from 'lodash'
import { apolloClient } from '~/common/apollo'
import { CREATE_SAVE_MUTATION, UPDATE_SAVE_MUTATION } from '~/common/apollo/mutations/jobs-filter.mutation'
import { SAVE_FILTER_QUERY } from '~/common/apollo/queries/job-filter.query'
import { FIRST, JOB_TYPES } from '~/common/constants'
import { mappingFilterJobSearch, mappingStringToLocation } from '~/common/helpers/mapping.helper'
import { action, computed, observable, store } from '~/common/mobx.decorator'
import { careStore } from '~/companies/care/care.store'
import { jobAlertsStore } from '~/components/job-alert-drawer/job-alert-drawer.store'
import { authStore, masterStore, notifyStore, routerStore } from '~/stores'
import { AvailableJobStore, FeaturedJobStore } from '~/stores/jobs'
import flatten, { unflatten } from 'flat'
import { MatchesJobStore } from '~/stores/jobs/matches.job.store'

@store()
class DiscoveryStore {
	@observable defaultSavedSearch
	@observable jobType = ''
	@observable jobFilter = {}
	@observable currentPage = 1
	@observable sortKey = 'NEWEST'
	@observable filterValues = {}
	@observable activeFilterId = ''
	@observable activeFilter = {}
	@observable listSaveSearchView = []
	@observable newSaveSearchView = {}
	availableJobStore = new AvailableJobStore()
	featuredJobStore = new FeaturedJobStore()
	matchesJobStore = new MatchesJobStore()

	@computed
	get initialValues() {
		let initialValues = {}

		// Only set addressCountries if it exists in jobFilter
		if (this.jobFilter?.addressCountries?.[0]) {
			initialValues.addressCountries = this.jobFilter.addressCountries[0]
		}
		if (this.jobFilter.specialtyIds) {
			const specialties = masterStore.onlySpecialtyOptions.filter((item) => {
				return this.jobFilter.specialtyIds.includes(item.value)
			})
			initialValues.specialty = specialties
		}

		if (this.jobFilter?.disciplineIds) {
			initialValues.disciplineId = this.jobFilter.disciplineIds?.[0]
		}

		if (this.jobFilter?.businessSectorNames) {
			initialValues.businessSectorNames = this.jobFilter.businessSectorNames
		}

		let location = []

		if (this.jobFilter?.addressCities) {
			location = mappingStringToLocation(this.jobFilter.addressCities)
		}

		if (this.jobFilter?.addressStates) {
			location = [...location, ...mappingStringToLocation(this.jobFilter.addressStates, true)]
		}

		if (location.length > 0) {
			initialValues.location = location
		}

		if (this.jobFilter?.nearBy?.radius) {
			initialValues.nearBy = {
				radius: this.jobFilter.nearBy.radius,
			}
		}

		return initialValues
	}

	@computed
	get hasNextPage() {
		switch (this.jobType) {
			case JOB_TYPES.MATCHES:
				return this.matchesJobStore?.totalCount > 0
			case JOB_TYPES.FEATURED:
				return this.featuredJobStore?.totalCount > 0
			default:
				return this.availableJobStore?.totalCount > 0
		}
	}

	@computed
	get searchResult() {
		switch (this.jobType) {
			case JOB_TYPES.MATCHES:
				return this.matchesJobStore.list
			case JOB_TYPES.FEATURED:
				return this.featuredJobStore.list
			default:
				return this.availableJobStore.list
		}
	}

	@computed
	get searchResultCount() {
		switch (this.jobType) {
			case JOB_TYPES.MATCHES:
				return this.matchesJobStore?.totalCount ?? 0
			case JOB_TYPES.FEATURED:
				return this.featuredJobStore?.totalCount ?? 0
			default:
				return this.availableJobStore?.totalCount ?? 0
		}
	}

	@computed
	get totalPage() {
		return Math.ceil(this.searchResultCount / FIRST)
	}

	@computed
	get isReady() {
		switch (this.jobType) {
			case JOB_TYPES.MATCHES:
				return this.matchesJobStore.isReady
			case JOB_TYPES.FEATURED:
				return this.featuredJobStore.isReady
			default:
				return this.availableJobStore.isReady
		}
	}

	@action
	setDefaultSavedSearch = (opt) => {
		this.defaultSavedSearch = opt
	}

	@action
	setJobType = (type) => {
		this.jobType = type
	}

	@action
	handleClickBookmark = (job) => {
		return this.availableJobsItems.map((item) => {
			item.hasBookmark = !item.hasBookmark

			return item
		})
	}

	@action
	setJobFilter = (filter) => {
		this.jobFilter = filter
	}

	@action
	setCurrentPage = (page) => {
		this.currentPage = page
	}

	@action
	setSortKey = (key) => {
		this.sortKey = key
	}

	@action
	resetJobFilter = () => {
		this.jobFilter = {}
	}

	@action
	fetchJobs = async (isReload, after) => {
		const sortBy = { field: this.sortKey, order: ['HARD_MATCHED', 'NEWEST'].includes(this.sortKey) ? 'desc' : 'asc' }

		switch (this.jobType) {
			case JOB_TYPES.MATCHES:
				await this.matchesJobStore.fetchData(isReload, this.jobFilter, { first: FIRST, after }, sortBy)
				break
			case JOB_TYPES.FEATURED:
				await this.featuredJobStore.fetchData(isReload, this.jobFilter, { first: FIRST, after }, sortBy)
				break
			default:
				await this.availableJobStore.fetchData(isReload, this.jobFilter, { first: FIRST, after }, sortBy)
				break
		}
	}

	@action
	getFlattenFilter = (value) => {
		return omit(value, ['workerId', 'companyId', 'id', 'radius', 'isActive'])
	}

	@action
	handleSaveSearch = async (values) => {
		const valueFilter = values.hasOwnProperty('specialtyIds')
			? {
					...values,
					specialtyIds: values?.specialtyIds.map((item) => item.value),
			  }
			: values
		const filteredArray = Object.entries(valueFilter).filter(([key, value]) => value !== undefined)
		const filteredObject = Object.fromEntries(filteredArray)
		const nameSaved = filteredObject?.name
		const dataSave = omit(filteredObject, ['name', 'isActive'])
		const disciplines = dataSave?.disciplines
		const skillNameLength = dataSave?.specialtyIds?.length
		const restValue =
			!!disciplines && skillNameLength === 0
				? {
						...dataSave,
						specialtyIds: this.disciplineOptions(disciplines).map((item) => item?.value),
				  }
				: dataSave

		const filterCondtionsLogin = omit(mappingFilterJobSearch(restValue), ['specialties'])
		const { frequency, alertChannels, enableAlert, pauseAlert, pauseAlertPeriod, pauseAlertUntil } = restValue || {}
		const specificScope = new URLSearchParams(routerStore.location?.search).get('type')

		const variables = {
			name: nameSaved,
			frequency: frequency,
			enableAlert: enableAlert,
			alertChannels: alertChannels,
			pauseAlert: pauseAlert,
			pauseAlertPeriod: pauseAlertPeriod,
			pauseAlertUntil: pauseAlertUntil,
			filterCondition: omit(filterCondtionsLogin, ['locationStates', 'locationCities']),
			specificScope: specificScope,
		}
		try {
			if (authStore.id) {
				const { data } = await apolloClient.mutate({
					mutation: CREATE_SAVE_MUTATION,
					variables: variables,
				})
				const id = data?.createSavedFilter?.id
				await this.fetchSaveSearhFilter()
				jobAlertsStore.setMode(jobAlertsStore.MODE_LIST.view)
				await notifyStore.success('$MESSAGES.SUCCESS_ADD_NEW_SAVE_SEARCH')
				await jobAlertsStore.setJobAlertId(id)
			}
		} catch (error) {
			notifyStore.error(error.message)
		}
	}

	@action
	handleUpdateSearch = async (values) => {
		const valueFilter = values.hasOwnProperty('specialtyIds')
			? {
					...values,
					specialtyIds: values?.specialtyIds.map((item) => item.value),
			  }
			: values

		const nameSaved = valueFilter?.name
		const dataSave = omit(valueFilter, ['name'])
		const id = valueFilter?.id
		const filterConditions = omit(mappingFilterJobSearch(this.handleFilterData(dataSave)), ['radius', 'isActive'])
		const { frequency, alertChannels, enableAlert, pauseAlert, pauseAlertPeriod, pauseAlertUntil } = valueFilter || {}
		const specificScope = new URLSearchParams(routerStore.location?.search).get('type')
		const variables = {
			id: id,
			name: nameSaved,
			frequency: frequency,
			enableAlert: enableAlert,
			alertChannels: alertChannels,
			pauseAlert: pauseAlert,
			pauseAlertPeriod: pauseAlertPeriod,
			pauseAlertUntil: pauseAlertUntil,
			filterCondition: omit(filterConditions, ['locationStates', 'locationCities']),
			specificScope: specificScope,
		}

		if (authStore.id) {
			try {
				const { data } = await apolloClient.mutate({
					mutation: UPDATE_SAVE_MUTATION,
					variables: variables,
				})

				await this.fetchSaveSearhFilter()
				jobAlertsStore.setMode(jobAlertsStore.MODE_LIST.view)

				await notifyStore.success('$MESSAGES.SUCCESS_UPDATE_SAVE_SEARCH')
				const id = data?.updateSavedFilter?.id
				await jobAlertsStore.setJobAlertId(id)
			} catch (error) {
				notifyStore.error(error.message)
			}
		}
	}

	@action
	fetchSaveSearhFilter = async () => {
		if (authStore.id) {
			const response = await apolloClient.query({
				query: SAVE_FILTER_QUERY,
			})

			const specificScope = new URLSearchParams(routerStore.location?.search).get('type') || 'available_jobs'

			this.listSaveSearchView = response?.data?.savedFilters?.nodes
				?.filter((item) => item?.specificScope === specificScope)
				.map((item) => {
					const { filterCondition } = item
					const { conditions } = filterCondition || {}
					const { licenseSpecialtyOptions, businessSectorNames } = masterStore

					const specialtyIds = licenseSpecialtyOptions?.filter((item) => conditions?.specialtyIds?.includes(item?.value))

					// Map businessSectorNames from ids to names
					const mappedBusinessSectors =
						conditions?.businessSectorNames?.map((sectorId) => {
							const sector = businessSectorNames.find((s) => s.value === sectorId)
							return sector?.label || sectorId
						}) || []

					if (item.id === this.activeFilterId) {
						return {
							...item,
							...item.filterCondition.conditions,
							disciplinesCategories: item.filterCondition?.conditions?.disciplinesCategories?.[0],
							specialtyIds,
							mappedBusinessSectorsName: mappedBusinessSectors,
							isActive: true,
						}
					} else {
						return {
							...item,
							...item.filterCondition.conditions,
							disciplinesCategories: item.filterCondition?.conditions?.disciplinesCategories?.[0],
							specialtyIds,
							mappedBusinessSectorsName: mappedBusinessSectors,
							isActive: false,
						}
					}
				})
		}
	}

	@action
	handleFilterData(dataFilter) {
		const { jobId, radius, ...values } = dataFilter

		const disciplines = dataFilter.disciplines
		const skillNameLength = dataFilter.specialties?.length
		if ((!!disciplines && skillNameLength === 0) || (!!disciplines && !skillNameLength)) {
			values.specialties = this.disciplineOptions(disciplines).map((item) => item?.value)
		}

		if (isArray(values?.locationState)) {
			values.addressCities = null
		}

		const filterRadius = omit(values, ['radius'])
		const data = flatten(filterRadius)
		// const normalizeData = omitBy(data, (value) => (typeof value === 'object' ? isEmpty(value) : !value))

		return isArray(values?.locationState)
			? unflatten({
					...data,
					locationCity: null,
			  })
			: unflatten({ ...data })
	}

	@action
	setActiveFilterId = (id) => {
		this.activeFilterId = id
	}

	@action
	setActiveFilter = (item) => {
		this.activeFilter = item
	}

	@action
	setNewSaveSearchView = (item) => {
		this.newSaveSearchView = item
	}

	@computed get getActiveFilterId() {
		return this.listSaveSearchView?.every((item) => item?.isActive === false)
	}
}

export const discoveryStore = new DiscoveryStore()
